'use client';

import { useCallback, useEffect, useRef } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { SpinningLoader } from '@/app_components/icons/SpinningLoader';

interface SignInPasswordResetModalProps {
  setResetModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  loading: boolean;
  resetSuccess: boolean;
}

export const SignInPasswordResetModal = ({
  setResetModalOpen,
  email,
  loading,
  resetSuccess,
}: SignInPasswordResetModalProps) => {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const closeModal = useCallback(() => {
    setResetModalOpen(false);
  }, [setResetModalOpen]);

  useEffect(() => {
    closeBtnRef.current?.focus();

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalBodyRef.current && !modalBodyRef.current.contains(e.target as Node)) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeModal]);

  const title = resetSuccess ? 'Check your email' : 'Something went wrong';
  const description = resetSuccess
    ? `A password reset link has been sent to ${email}.`
    : "We couldn't send a reset link. Please try again.";

  return (
    <div
      aria-describedby='reset-modal-desc'
      aria-labelledby='reset-modal-title'
      aria-modal='true'
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/48'
      role='dialog'
    >
      <div
        className='relative z-10 flex w-[328px] flex-col items-center gap-xl rounded-md bg-pure px-6 py-10 md:w-[450px] md:gap-10 md:px-10 md:py-16'
        ref={modalBodyRef}
      >
        <ActionableIcon
          aria-label='close dialog'
          className='absolute top-4 right-4'
          onClick={closeModal}
          ref={closeBtnRef}
          type='button'
        >
          <Icon icon='X' />
        </ActionableIcon>
        {loading ? (
          <SpinningLoader />
        ) : (
          <>
            <h2
              className='typography-level-2 font-graphik-compact font-medium text-center'
              id='reset-modal-title'
            >
              {title}
            </h2>
            <p
              className='typography-level-6 font-graphik-compact text-center'
              id='reset-modal-desc'
            >
              {description}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
